import { useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { RotateCcw, Save } from 'lucide-react';
import { useWorkoutStore } from '../stores/workoutStore';
import { DayCard } from './DayCard';
import { WeekFeedback } from './WeekFeedback';
import { sortDates, formatDateRange, formatWorkoutDate } from '../lib/utils';
import { walkSeasonWeeks, getDaySessions, getDayLabel } from '../types/workout';
import type { DayData, Session } from '../types/workout';
import { cn } from '../lib/utils';

const WEEK_DROP_PREFIX = 'week-drop:';
const SESSION_DRAG_PREFIX = 'session:';

/**
 * Drop target for "drag a day from the active calendar onto a different week".
 * Drop is interpreted as: place the dragged day at the same day-of-week slot
 * in the target week, swapping content with whatever lives there. If no slot
 * matches, the day is appended to the target week with the original date.
 */
function WeekChip({
  index,
  isActive,
  totalCycles,
  cycleIndex,
  onClick,
  label,
  range,
}: {
  index: number;
  isActive: boolean;
  totalCycles: number;
  cycleIndex: number;
  onClick: () => void;
  label: string;
  range: string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `${WEEK_DROP_PREFIX}${index}` });
  return (
    <button
      ref={setNodeRef}
      onClick={onClick}
      className={cn(
        'flex flex-col items-start rounded-md border px-2 py-1 text-xs transition-colors min-w-[100px]',
        isActive
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border bg-card text-muted-foreground hover:bg-accent',
        isOver && 'ring-2 ring-emerald-500 ring-offset-1',
      )}
      title="Click to view, or drop a day here to move it to this week"
    >
      <span className="font-semibold">
        {label}
        {totalCycles > 1 && (
          <span className="ml-1 text-[10px] font-normal text-muted-foreground/80">
            · C{cycleIndex + 1}
          </span>
        )}
      </span>
      <span className="text-[10px]">{range}</span>
    </button>
  );
}

/**
 * Visual clone of a session tile, rendered in the DragOverlay portal so the
 * cursor carries something while the source tile stays in place. Without the
 * portal, the real tile is moved via CSS transforms and fights the
 * overflow-x-auto calendar grid, making cross-day drags feel jumpy.
 */
function SessionDragPreview({ session }: { session: Session }) {
  const exerciseCount = Array.isArray(session.exercises) ? session.exercises.length : 0;
  return (
    <div className="w-72 cursor-grabbing rounded-md border border-blue-300 bg-white p-2.5 shadow-2xl">
      <div className="flex items-center gap-1.5">
        {session.category && (
          <span className="rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
            {session.category}
          </span>
        )}
        {session.name && session.name !== session.category && (
          <span className="truncate text-xs font-medium text-gray-800">{session.name}</span>
        )}
      </div>
      {exerciseCount > 0 && (
        <p className="mt-1 text-[10px] text-gray-400">
          {exerciseCount} exercise{exerciseCount === 1 ? '' : 's'}
        </p>
      )}
    </div>
  );
}

/** Minimal day-card preview for the DragOverlay during a day-level drag. */
function DayDragPreview({ date, label }: { date: string; label: string }) {
  return (
    <div className="w-72 cursor-grabbing rounded-lg border border-blue-300 bg-white p-3 shadow-2xl">
      <h3 className="font-semibold text-gray-900">{formatWorkoutDate(date)}</h3>
      {label && <p className="text-sm text-gray-600">{label}</p>}
    </div>
  );
}

/** What the user is currently dragging — set by onDragStart, cleared on end/cancel. */
type ActiveDrag =
  | { kind: 'session'; date: string; sessionIndex: number; session: Session }
  | { kind: 'day'; date: string; dayData: DayData }
  | null;

export function WeekCalendar() {
  const {
    season,
    swapDays,
    moveDayToDate,
    swapSessions,
    moveSessionToDay,
    currentWeekIndex,
    nextWeek,
    previousWeek,
    setCurrentWeekIndex,
    isDirty,
    isSaving,
    saveChanges,
    discardChanges,
  } = useWorkoutStore();

  // One shared sensor set for both day and session drags. The 4px activation
  // distance keeps a plain click (title/goal click-to-edit) from starting a
  // drag — this used to live on DayCard's inner context.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [activeDrag, setActiveDrag] = useState<ActiveDrag>(null);

  if (!season) return null;

  // Flatten weeks across every cycle so navigation works for multi-cycle seasons.
  const flattened = walkSeasonWeeks(season);
  const totalWeeks = flattened.length;
  const safeIndex = Math.min(Math.max(currentWeekIndex, 0), Math.max(totalWeeks - 1, 0));
  const current = flattened[safeIndex];

  if (!current) return null;
  const { week, cycleIndex } = current;
  const totalCycles = season.cycles.length;

  const dates = sortDates(Object.keys(week.days));

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    if (id.startsWith(SESSION_DRAG_PREFIX)) {
      const [, srcDate, idxStr] = id.split(':');
      const sessionIndex = parseInt(idxStr, 10);
      if (!srcDate || Number.isNaN(sessionIndex)) return;
      // Find the session payload across every cycle (cross-week safe).
      for (const { week: w } of walkSeasonWeeks(season)) {
        const day = w.days[srcDate];
        if (!day) continue;
        const sess = getDaySessions(day)[sessionIndex];
        if (sess) {
          setActiveDrag({ kind: 'session', date: srcDate, sessionIndex, session: sess });
          return;
        }
      }
      return;
    }
    // Day drag — the id is the date itself.
    for (const { week: w } of walkSeasonWeeks(season)) {
      const day = w.days[id];
      if (day) {
        setActiveDrag({ kind: 'day', date: id, dayData: day });
        return;
      }
    }
  };

  const handleDragCancel = () => setActiveDrag(null);

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    if (activeId.startsWith(SESSION_DRAG_PREFIX)) {
      const [, sourceDate, sourceIdxStr] = activeId.split(':');
      const sourceIndex = parseInt(sourceIdxStr, 10);
      if (!sourceDate || Number.isNaN(sourceIndex)) return;

      if (overId.startsWith(SESSION_DRAG_PREFIX)) {
        const [, targetDate, targetIdxStr] = overId.split(':');
        const targetIndex = parseInt(targetIdxStr, 10);
        if (!targetDate || Number.isNaN(targetIndex)) return;

        if (sourceDate === targetDate) {
          // Same-day reorder (was DayCard's inner context).
          if (sourceIndex === targetIndex) return;
          swapSessions(sourceDate, sourceIndex, targetIndex);
        } else {
          moveSessionToDay(sourceDate, sourceIndex, targetDate);
        }
        return;
      }

      if (overId.startsWith(WEEK_DROP_PREFIX)) return; // week chips: not a session target
      if (overId === sourceDate) return; // back onto its own day → no-op
      moveSessionToDay(sourceDate, sourceIndex, overId);
      return;
    }

    if (activeId === overId) return;

    if (overId.startsWith(WEEK_DROP_PREFIX)) {
      const targetIdx = parseInt(overId.slice(WEEK_DROP_PREFIX.length), 10);
      if (Number.isNaN(targetIdx) || targetIdx === safeIndex) return;
      const targetWeek = flattened[targetIdx]?.week;
      if (!targetWeek) return;

      const sourceDow = new Date(activeId + 'T12:00:00').getDay();
      const targetDates = Object.keys(targetWeek.days);
      const sameDow = targetDates.find((d) => new Date(d + 'T12:00:00').getDay() === sourceDow);
      const targetDate = sameDow ?? targetDates.sort()[0] ?? null;
      if (!targetDate) return;
      moveDayToDate(activeId, targetDate);
      return;
    }

    swapDays(activeId, overId);
  };

  return (
    <div className="min-w-0">
      <div className="mb-4 p-3 bg-card rounded-lg border">
        <div className="flex items-center justify-between">
          {totalWeeks > 1 ? (
            <button
              onClick={previousWeek}
              disabled={safeIndex === 0}
              className="px-3 py-1.5 text-sm border rounded-md hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Prev
            </button>
          ) : (
            <span className="w-[60px]" />
          )}

          <div className="text-center">
            <p className="text-sm font-semibold flex items-center justify-center gap-2">
              Week {safeIndex + 1} of {totalWeeks}
              {totalCycles > 1 && (
                <span className="text-xs font-normal text-muted-foreground">
                  · Cycle {cycleIndex + 1} / {totalCycles}
                </span>
              )}
              {isDirty && (
                <span
                  title="You have unsaved workout edits"
                  className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                >
                  <span className="size-1 rounded-full bg-amber-500" />
                  Unsaved
                </span>
              )}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatDateRange(week.days)}
            </p>
          </div>

          {totalWeeks > 1 ? (
            <button
              onClick={nextWeek}
              disabled={safeIndex >= totalWeeks - 1}
              className="px-3 py-1.5 text-sm border rounded-md hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          ) : (
            <span className="w-[60px]" />
          )}
        </div>

        {/* Save / Refresh — operate on the whole season, but sit directly
            under the Week N of N label so they're at hand while editing. */}
        <div className="mt-3 flex items-center justify-center gap-2 border-t pt-3">
          <button
            onClick={discardChanges}
            disabled={!isDirty || isSaving}
            title={
              !isDirty
                ? 'No unsaved edits'
                : 'Discard local edits and reload the last saved version'
            }
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-input bg-background rounded-md hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCcw className="size-3.5" />
            Refresh
          </button>
          <button
            onClick={saveChanges}
            disabled={!isDirty || isSaving}
            title={!isDirty ? 'No edits to save' : 'Push edits to the user'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="size-3.5" />
            {isSaving ? 'Saving...' : 'Save workouts'}
          </button>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {/* Week strip — clickable + droppable for cross-week day moves */}
        {totalWeeks > 1 && (
          <div className="mb-3 flex flex-wrap gap-1.5">
            {flattened.map((entry, i) => (
              <WeekChip
                key={`${entry.cycleIndex}-${entry.weekIndex}-${i}`}
                index={i}
                isActive={i === safeIndex}
                totalCycles={totalCycles}
                cycleIndex={entry.cycleIndex}
                onClick={() => setCurrentWeekIndex(i)}
                label={`Wk ${i + 1}`}
                range={formatDateRange(entry.week.days)}
              />
            ))}
          </div>
        )}

        {week.goal && (
          <div className="mb-4 p-3 bg-card rounded-lg border">
            <h3 className="font-semibold text-sm mb-1">Week Goal</h3>
            <p className="text-sm text-muted-foreground">{week.goal}</p>
            {week.priorities && week.priorities.length > 0 && (
              <div className="mt-1">
                <span className="text-xs font-medium text-muted-foreground">Priorities: </span>
                <span className="text-xs text-muted-foreground">
                  {week.priorities.join(', ')}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="overflow-x-auto pb-2">
          <div className="flex gap-3 min-w-min">
            {dates.map((date) => (
              <div key={date} className="shrink-0 w-72">
                <DayCard date={date} dayData={week.days[date]} />
              </div>
            ))}
          </div>
        </div>

        {/* Portal'd clone that follows the cursor. Without it, the real
            session/day element is translated via CSS transforms and fights
            the overflow-x-auto grid, making cross-day drags feel jumpy. */}
        <DragOverlay dropAnimation={null}>
          {activeDrag?.kind === 'session' && (
            <SessionDragPreview session={activeDrag.session} />
          )}
          {activeDrag?.kind === 'day' && (
            <DayDragPreview date={activeDrag.date} label={getDayLabel(activeDrag.dayData)} />
          )}
        </DragOverlay>
      </DndContext>

      <div className="mt-4 p-3 bg-accent/50 rounded-lg border">
        <p className="text-xs text-muted-foreground">
          <strong>Tip:</strong> Drag a day card to swap with another in the same week, or onto a Week chip above to move it across weeks. Drag a session's handle (⠿) to reorder within a day, or onto a different day card to move the whole session (exercises and sets/reps come with it). Click an exercise to edit inline. Use the Move… action in a day's menu to pick an exact date.
        </p>
      </div>

      {/* Admin validation: rate this week's overall structure (day-to-day
          progression, intensity arc, exercise rotation) — separate signal
          from rating individual days. Anchored on the week's earliest date. */}
      {dates.length > 0 && <WeekFeedback weekStart={dates[0]} />}
    </div>
  );
}
